import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import "./Blogs.css";
import api from "../../Api"; // 👈 your existing axios instance
import Navber from "../../Components/Navber.jsx";

/* ============================================================
   HELPERS
   ============================================================ */

const escapeHtml = (s = "") =>
  s.replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/>/g, "&gt;");

const escapeAttr = (s = "") =>
  s
    .replace(/&/g, "&amp;")
    .replace(/"/g, "&quot;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;");

// Matches http(s)://... OR www....
const URL_REGEX = /((?:https?:\/\/|www\.)[^\s<>"']+)/gi;

// Characters we don't want to include at the very end of a URL
const TRAILING_PUNCT_REGEX = /[.,;:!?)\]}'"]+$/;

const splitTrailingPunctuation = (url) => {
  const m = url.match(TRAILING_PUNCT_REGEX);
  if (!m) return [url, ""];
  const trailing = m[0];
  return [url.slice(0, -trailing.length), trailing];
};

/**
 * Walks every text node in the doc body (skipping text already inside <a>)
 * and replaces plain-text URLs with real <a target="_blank"> elements.
 */
const linkifyTextNodes = (doc) => {
  const walker = doc.createTreeWalker(doc.body, NodeFilter.SHOW_TEXT);
  const textNodesToProcess = [];
  let node;

  while ((node = walker.nextNode())) {
    // Skip text already inside an <a>
    if (node.parentElement?.closest("a")) continue;

    URL_REGEX.lastIndex = 0;
    if (URL_REGEX.test(node.nodeValue)) {
      textNodesToProcess.push(node);
    }
    URL_REGEX.lastIndex = 0;
  }

  textNodesToProcess.forEach((textNode) => {
    const text = textNode.nodeValue;
    const fragment = doc.createDocumentFragment();
    let lastIndex = 0;
    let match;

    URL_REGEX.lastIndex = 0;
    while ((match = URL_REGEX.exec(text)) !== null) {
      const rawUrl = match[0];
      const start = match.index;
      const [cleanUrl, trailingPunct] = splitTrailingPunctuation(rawUrl);

      // Text before the URL
      if (start > lastIndex) {
        fragment.appendChild(
          doc.createTextNode(text.slice(lastIndex, start))
        );
      }

      // The <a> element
      const a = doc.createElement("a");
      a.setAttribute(
        "href",
        cleanUrl.startsWith("www.") ? `https://${cleanUrl}` : cleanUrl
      );
      a.setAttribute("target", "_blank");
      a.setAttribute("rel", "noopener noreferrer");
      a.textContent = cleanUrl;
      fragment.appendChild(a);

      // Any trailing punctuation after the URL
      if (trailingPunct) {
        fragment.appendChild(doc.createTextNode(trailingPunct));
      }

      lastIndex = start + rawUrl.length;
    }

    // Remaining text after the last URL
    if (lastIndex < text.length) {
      fragment.appendChild(doc.createTextNode(text.slice(lastIndex)));
    }

    textNode.parentNode.replaceChild(fragment, textNode);
  });
};

/**
 * Builds a plain-text excerpt (max `maxLen` chars) from HTML.
 * - Auto-linkifies plain-text URLs (http/https/www) -> clickable <a> tags
 * - Keeps existing <a> tags (so links remain clickable)
 * - Forces every link to open in a new tab (target="_blank")
 * - Strips all other tags
 * - Escapes text/href for safety
 * - Auto-closes anchors that get cut off by truncation
 */
const buildExcerpt = (html, maxLen = 140) => {
  if (!html) return "";

  let doc;
  try {
    doc = new DOMParser().parseFromString(String(html), "text/html");
  } catch {
    return escapeHtml(String(html)).slice(0, maxLen) + "...";
  }

  // 🔑 Step 1: turn plain-text URLs into real <a> tags
  linkifyTextNodes(doc);

  // 🔑 Step 2: collect tokens while preserving <a> tags, stripping others
  const tokens = [];

  const walk = (nodes) => {
    for (const node of nodes) {
      if (node.nodeType === Node.TEXT_NODE) {
        if (node.textContent) {
          tokens.push({ type: "text", value: node.textContent });
        }
      } else if (node.nodeType === Node.ELEMENT_NODE) {
        if (node.tagName === "A" && node.getAttribute("href")) {
          tokens.push({ type: "openA", href: node.getAttribute("href") });
          walk(node.childNodes);
          tokens.push({ type: "closeA" });
        } else {
          // strip tag, keep children
          walk(node.childNodes);
        }
      }
    }
  };
  walk(doc.body.childNodes);

  let result = "";
  let count = 0;
  let truncated = false;
  const openAnchors = [];

  for (const token of tokens) {
    if (token.type === "closeA") {
      if (openAnchors.length) {
        result += "</a>";
        openAnchors.pop();
      }
      continue;
    }

    if (count >= maxLen) {
      truncated = true;
      break;
    }

    if (token.type === "text") {
      const text = token.value.replace(/\s+/g, " ");
      const remaining = maxLen - count;

      if (text.length <= remaining) {
        result += escapeHtml(text);
        count += text.length;
      } else {
        result += escapeHtml(text.slice(0, remaining));
        count = maxLen;
        truncated = true;
      }
    } else if (token.type === "openA") {
      result += `<a href="${escapeAttr(
        token.href
      )}" target="_blank" rel="noopener noreferrer" class="jayraj-blog-excerpt-link">`;
      openAnchors.push(true);
    }
  }

  // close any anchors left open by truncation
  while (openAnchors.length) {
    result += "</a>";
    openAnchors.pop();
  }

  return result.trim() + (truncated ? "..." : "");
};

/* ============================================================
   COMPONENT
   ============================================================ */

const BlogPage = () => {
  const navigate = useNavigate();
  const [posts, setPosts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeCategory, setActiveCategory] = useState("All");

  const categories = ["All", "Destinations", "Travel Tips", "Food", "Guides"];

  /* ---------------- FETCH LIST ---------------- */
  useEffect(() => {
    const fetchPosts = async () => {
      try {
        const res = await api.get("/happy-customers");
        if (res.data?.status === true) {
          setPosts(res.data.data || []);
        }
      } catch (err) {
        console.error("Error fetching happy customers:", err);
      } finally {
        setLoading(false);
      }
    };
    fetchPosts();
  }, []);

  /* ---------------- FILTER ---------------- */
  const filteredPosts =
    activeCategory === "All"
      ? posts
      : posts.filter((post) => post.category === activeCategory);

  const handleCardClick = (slug) => {
    navigate(`/blogs/${slug}`);
  };

  /* ---------------- LINK CLICK (inside card) ----------------
     If the user clicks a link inside the description excerpt,
     DON'T trigger the card navigation — let the link open
     normally in the new tab.
  ------------------------------------------------------------- */
  const handleExcerptClick = (e) => {
    const anchor = e.target.closest("a");
    if (anchor) {
      e.stopPropagation();
      // anchor already has target="_blank" + rel="noopener noreferrer"
    }
  };

  return (
    <div className="jayraj-blog-page">
      <Navber />

      {/* ================= HERO BANNER ================= */}
      <section className="jayraj-blog-hero">
        <div className="jayraj-blog-hero-bg">
          <img
            src="https://images.unsplash.com/photo-1524492412937-b28074a5d7da?auto=format&fit=crop&w=1600&q=80"
            alt="Travel blog hero"
          />
          <div className="jayraj-blog-hero-overlay" />
        </div>

        <div className="jayraj-blog-hero-content">
          <span className="jayraj-blog-hero-tag">Jayraj Journal</span>
          <h1 className="jayraj-blog-hero-title">
            Stories, Guides & Wanderlust
          </h1>
          <p className="jayraj-blog-hero-subtitle">
            Curated travel stories, insider tips, and destination guides —
            written by travellers, for travellers.
          </p>
        </div>
      </section>

      {/* ================= CATEGORY FILTER ================= */}
      {/* <section className="jayraj-blog-filter-wrap">
        <div className="jayraj-blog-filter">
          {categories.map((cat) => (
            <button
              key={cat}
              className={`jayraj-blog-filter-btn ${
                activeCategory === cat ? "active" : ""
              }`}
              onClick={() => setActiveCategory(cat)}
            >
              {cat}
            </button>
          ))}
        </div>
      </section> */}

      {/* ================= BLOG CARDS ================= */}
      <section className="jayraj-blog-grid-wrap">
        {loading ? (
          <p className="jayraj-blog-empty">Loading stories...</p>
        ) : (
          <div className="jayraj-blog-grid">
            {filteredPosts.map((post) => (
              <article
                className="jayraj-blog-card"
                key={post.id}
                onClick={() => handleCardClick(post.slug)}
              >
                <div className="jayraj-blog-card-image">
                  <img
                    src={
                      post.images?.[0]?.url ||
                      "https://images.unsplash.com/photo-1488646953014-85cb44e25828?auto=format&fit=crop&w=800&q=80"
                    }
                    alt={post.title}
                    loading="lazy"
                  />
                  <span className="jayraj-blog-card-category">
                    Happy Customer
                  </span>
                </div>

                <div className="jayraj-blog-card-body">
                  <div className="jayraj-blog-card-meta">
                    <span>
                      {new Date(post.created_at).toLocaleDateString("en-IN", {
                        day: "2-digit",
                        month: "short",
                        year: "numeric",
                      })}
                    </span>
                    <span className="jayraj-blog-meta-dot">•</span>
                    <span>
                      {post.images?.length || 0} photos
                      {post.videos?.length
                        ? ` · ${post.videos.length} videos`
                        : ""}
                    </span>
                  </div>

                  <h3 className="jayraj-blog-card-title">{post.title}</h3>

                  {/* === DESCRIPTION EXCERPT (keeps links, opens in new tab) === */}
                  <p
                    className="jayraj-blog-card-excerpt"
                    onClick={handleExcerptClick}
                    dangerouslySetInnerHTML={{
                      __html: buildExcerpt(post.description, 140),
                    }}
                  />

                  <div className="jayraj-blog-card-footer">
                    <span className="jayraj-blog-card-author">
                      Jayraj Tours
                    </span>
                    <span className="jayraj-blog-card-read">
                      Read More →
                    </span>
                  </div>
                </div>
              </article>
            ))}
          </div>
        )}

        {!loading && filteredPosts.length === 0 && (
          <p className="jayraj-blog-empty">No posts in this category yet.</p>
        )}
      </section>
    </div>
  );
};

export default BlogPage;